import React, { useEffect, useState, useCallback, forwardRef } from 'react';
import { Calendar } from 'primereact/calendar';
import moment from 'moment-timezone';

const TimezoneDatePicker = forwardRef(({ value, onChange, timezone, isEndOfDay = true, ...rest }, calendarRef) => {
  const [localValue, setLocalValue] = useState(null);

  // Fallback to browser's timezone if not provided
  const effectiveTimezone = timezone || moment.tz.guess();

  /**
   * Convert UTC date to local timezone representation
   * Creates a new Date object with ONLY the date components (year, month, day)
   * This ensures the calendar displays the correct date regardless of time
   */
  const toLocal = useCallback(
    (utcValue) => {
      if (!utcValue) return null;
      const m = moment(utcValue).tz(effectiveTimezone);
      // Only use year, month, date - ignore time to avoid day shifting issues
      // The calendar only needs the date for display; time is handled during conversion
      return new Date(m.year(), m.month(), m.date());
    },
    [effectiveTimezone]
  );

  /**
   * Convert an array of UTC dates to local timezone representation
   */
  const toLocalRange = useCallback(
    (range) => {
      if (!Array.isArray(range)) return null;
      return range.map((d) => (d ? toLocal(d) : null));
    },
    [toLocal]
  );

  // Handle incoming value (UTC → timezone)
  useEffect(() => {
    if (!value) {
      setLocalValue(null);
      return;
    }
    if (Array.isArray(value)) {
      setLocalValue(toLocalRange(value));
    } else {
      setLocalValue(toLocal(value));
    }
  }, [value, effectiveTimezone, toLocal, toLocalRange]);

  const toUTC = useCallback(
    (local, useEndOfDay = isEndOfDay) => {
      if (!local) return null;

      // Determine hour, minute, second based on useEndOfDay
      const hour = useEndOfDay ? 23 : 0;
      const minute = useEndOfDay ? 59 : 0;
      const second = useEndOfDay ? 59 : 0;

      // Create a moment in the target timezone with the selected date and specified time
      // This matches the behavior of convertToTimezoneWithEndOfDayTime
      return moment
        .tz(
          {
            year: local.getFullYear(),
            month: local.getMonth(),
            date: local.getDate(),
            hour,
            minute,
            second,
          },
          effectiveTimezone
        )
        .format();
    },
    [effectiveTimezone, isEndOfDay]
  );

  /**
   * Convert an array of local dates to UTC
   * For date ranges: first date uses start of day (00:00:00), second date uses end of day (23:59:59)
   */
  const toUTCRange = useCallback(
    (range) => {
      if (!Array.isArray(range)) return null;
      return range.map((d, index) => {
        if (!d) return null;
        // First element (start date) should be start of day, second element (end date) should be end of day
        const useEndOfDay = index > 0;
        return toUTC(d, useEndOfDay);
      });
    },
    [toUTC]
  );

  // When user selects date(s)
  const handleChange = useCallback(
    (e) => {
      const picked = e.value;
      if (!picked) {
        onChange(null);
        return;
      }
      if (Array.isArray(picked)) {
        onChange(toUTCRange(picked));
      } else {
        onChange(toUTC(picked));
      }
    },
    [onChange, toUTC, toUTCRange]
  );

  return <Calendar ref={calendarRef} value={localValue} onChange={handleChange} {...rest} />;
});

export default TimezoneDatePicker;
